import { useState } from 'react';
import { Label } from '@/components/shadcn/label';
import {
  SportDialogOutlineButton,
  SportDialogPrimaryButton,
  SportFormDialog,
} from '@/components/ui/SportFormDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import type { MabarParticipant } from '@/services/communityService';
import type { BadmintonGameListItem } from '@/services/badmintonService';
import badmintonService from '@/services/badmintonService';

interface BadmintonRoundLineupEditorProps {
  isOpen: boolean;
  onClose: () => void;
  game: BadmintonGameListItem;
  participants: MabarParticipant[];
  onSuccess: () => void;
  onError: (message: string) => void;
}

export default function BadmintonRoundLineupEditor({
  isOpen,
  onClose,
  game,
  participants,
  onSuccess,
  onError,
}: BadmintonRoundLineupEditorProps) {
  const slots = game.playerFormat === 'doubles' ? 2 : 1;
  const confirmed = participants.filter((p) => p.status === 'confirmed');
  const [sideA, setSideA] = useState<string[]>(
    game.sideAPlayers.map((p) => String(p.participantId))
  );
  const [sideB, setSideB] = useState<string[]>(
    game.sideBPlayers.map((p) => String(p.participantId))
  );
  const [submitting, setSubmitting] = useState(false);

  const pick = (side: 'a' | 'b', index: number, value: string) => {
    const setter = side === 'a' ? setSideA : setSideB;
    const current = side === 'a' ? [...sideA] : [...sideB];
    while (current.length < slots) current.push('');
    current[index] = value;
    setter(current);
  };

  const isRoundGame = game.roundNumber != null && game.sessionSlug != null;

  const handleSave = async () => {
    const a = sideA.map(Number).filter((n) => n > 0);
    const b = sideB.map(Number).filter((n) => n > 0);
    if (a.length !== slots || b.length !== slots) {
      onError('Lengkapi susunan pemain');
      return;
    }
    setSubmitting(true);
    try {
      if (isRoundGame) {
        const used = new Set([...a, ...b]);
        const queue = confirmed.map((p) => p.id).filter((id) => !used.has(id));
        await badmintonService.updateRoundLineup(game.sessionSlug, game.roundNumber as number, {
          sideA: a,
          sideB: b,
          queue,
        });
      } else {
        await badmintonService.updateGameLineup(game.slug, { sideA: a, sideB: b });
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      onError(msg || (err instanceof Error ? err.message : 'Gagal menyimpan susunan'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderSide = (side: 'a' | 'b', label: string) => {
    const values = side === 'a' ? sideA : sideB;
    const other = side === 'a' ? sideB : sideA;
    return (
      <div>
        <Label>{label}</Label>
        {Array.from({ length: slots }).map((_, i) => (
          <Select
            key={`${side}-${i}`}
            value={values[i] ?? ''}
            onValueChange={(v) => pick(side, i, v)}
          >
            <SelectTrigger className="mt-1 mb-2">
              <SelectValue placeholder="Pilih pemain" />
            </SelectTrigger>
            <SelectContent>
              {confirmed.map((p) => {
                const blocked =
                  other.includes(String(p.id)) ||
                  values.some((x, j) => j !== i && x === String(p.id));
                if (blocked) return null;
                return (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name || `Peserta #${p.id}`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        ))}
      </div>
    );
  };

  return (
    <SportFormDialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={`Edit Player${game.roundNumber != null ? ` · Ronde ${game.roundNumber}` : ''}`}
      description="Ganti pemain dari daftar peserta confirmed."
      footer={
        <>
          <SportDialogOutlineButton onClick={onClose} disabled={submitting}>
            Batal
          </SportDialogOutlineButton>
          <SportDialogPrimaryButton onClick={handleSave} disabled={submitting}>
            {submitting ? 'Menyimpan...' : 'Simpan'}
          </SportDialogPrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        {renderSide('a', 'Sisi A')}
        {renderSide('b', 'Sisi B')}
      </div>
    </SportFormDialog>
  );
}
